import React from "react";
import { usePlayer } from "@/context/PlayerContext";
import { srcForCover } from "@/lib/api";
import { formatDuration } from "@/lib/utils2";
import { Play, Pause, Volume2, Radio } from "lucide-react";

export default function AudioPlayer() {
  const { current, playing, progress, duration, volume, toggle, seek, changeVolume } = usePlayer();

  return (
    <div
      data-testid="sticky-audio-player"
      className="sticky top-0 z-40 w-full bg-brand-page/90 backdrop-blur-2xl backdrop-saturate-150 border-b border-brand-line"
    >
      <div className="px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center gap-4">
          {/* Now playing */}
          <div className="flex items-center gap-3 min-w-0 w-[38%] sm:w-[30%]">
            <div className="relative h-12 w-12 shrink-0 rounded-lg overflow-hidden border border-brand-line bg-brand-sidebar">
              {current ? (
                <img src={srcForCover(current)} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-brand-muted">
                  <Radio className="w-5 h-5" />
                </div>
              )}
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-brand-muted">Now Playing</p>
              <p data-testid="player-title" className="text-sm font-semibold truncate text-brand-ink">
                {current ? current.title : "Nothing loaded yet"}
              </p>
              <p className="text-xs text-brand-muted truncate">
                {current && current.hosts?.length ? current.hosts.join(" & ") : "The Spread"}
              </p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex-1 flex flex-col items-center gap-1">
            <button
              data-testid="player-toggle-button"
              onClick={() => toggle(current)}
              disabled={!current}
              className="h-11 w-11 rounded-full brand-btn flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform duration-300"
            >
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>
            <div className="w-full flex items-center gap-2">
              <span className="text-[11px] tabular-nums text-brand-muted w-9 text-right">{formatDuration(progress)}</span>
              <input
                data-testid="player-seek"
                type="range"
                min={0}
                max={duration || 0}
                value={progress}
                onChange={(e) => seek(Number(e.target.value))}
                disabled={!current}
                className="flex-1 h-1.5 accent-brand cursor-pointer"
                style={{ accentColor: "#4A7045" }}
              />
              <span className="text-[11px] tabular-nums text-brand-muted w-9">{formatDuration(duration)}</span>
            </div>
          </div>

          {/* Volume */}
          <div className="hidden md:flex items-center gap-2 w-[16%]">
            <Volume2 className="w-4 h-4 text-brand-muted shrink-0" />
            <input
              data-testid="player-volume"
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={volume}
              onChange={(e) => changeVolume(Number(e.target.value))}
              className="w-full h-1.5 cursor-pointer"
              style={{ accentColor: "#4A7045" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
